package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/remember_coroutine_scope")
@Composable
fun RememberCoroutineScopePage() {
    BlogLayout(
        PageProperties(
            "rememberCoroutineScope",
            "rememberCoroutineScope - (User Actions - Lifecycle-Aware Coroutines - Trigger coroutines from event handlers -  Launching Coroutines Outside Composition - Lives as long as the composable - Handle User-Initiated Actions - Automatically cancelled when the associated Composable is removed - Event-driven - Event-Based Work - Launch Coroutines from Event)"
        ),
        BlogProperties("Jetpack Compose", "rememberCoroutineScope"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "rememberCoroutineScope - (User Actions - Lifecycle-Aware Coroutines - Trigger coroutines from event handlers -  Launching Coroutines Outside Composition - Lives as long as the composable - Handle User-Initiated Actions - Automatically cancelled when the associated Composable is removed - Event-driven - Event-Based Work - Launch Coroutines from Event)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect is not scoped to the composable function. This means that the coroutine scope will not be cancelled when the composable function is removed from the composition hierarchy. This could lead to a memory leak, as the coroutine would continue to run in the background, even though it is no longer needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lifecycle Awareness: The coroutine launched via the scope will be automatically canceled when the composable is removed from the composition, preventing memory leaks, similar to LaunchEffect.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope, you no longer need to manually create and manage coroutine scopes. rememberCoroutineScope create (return) a CoroutineScope object (bound to the point of Composable where it is called), which allows you to manage operations that run on a separate thread from the UI thread using asynchronous coding techniques.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To ensure LaunchedEffect runs during the first composition, you can use it directly. However, if you need more control over when the coroutine starts, you can use rememberCoroutineScope. This function provides a coroutine scope that’s aware of the composable lifecycle, allowing you to launch coroutines outside the composable. The scope is tied to the composable where it's created, and it gets canceled when that composable leaves the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use rememberCoroutineScope for event-driven tasks, like user actions, where you need more control over coroutine execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "RememberCoroutineScope helps preserve the state of a CoroutineScope object, allowing it to be maintained during the reordering or iteration of UI elements such as widgets.  This ensures that operations are properly managed and prevents unnecessary errors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Enter rememberCoroutineScope, which provides a coroutine scope tied to the composable lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Helpful when you are using coroutines and need to manage the coroutine’s lifecycle within a Composable. This is particularly useful when you need to launch coroutines in response to user actions or other events that are not directly tied to recompositions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Work: A composable function that gives reference to a composition-aware coroutine scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The rememberCoroutineScope function in Jetpack Compose provides a coroutine scope that is not automatically canceled when the associated Composable is removed from the composition, as their lifecycle is managed by the Compose runtime. It is your responsibility to manage the lifecycle of the coroutine launched within this scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The CoroutineScope object created by rememberCoroutineScope() is a singleton for each composition. This means that if the function is called multiple times within the same composition, it will return the same coroutine scope object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Progress bar: I’m using regular CoroutineScope to run progress bar updates. As you can see the problem is that I’m trying to cancel the update, but I’m still getting the success message. This is because regular CoroutineScope is not aware of my Composable’s lifecycle. It will run even if the operation that it started is no longer relevant.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Progress bar: rememberCoroutineScope ensures that the coroutine lifecycle is tied to the progress bar Composable. The progress bar leaves the composition when I press “cancel” — so does the coroutine running the updates. A very simple and easy solution indeed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Animation: We animate a box's size and then its color change sequentially when a button is clicked. This sequential animation can't be directly managed in the `ViewModel` and benefits from `rememberCoroutineScope()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope() whenever you need to have your coroutine cancelled when some UI element leaves the Composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Launch",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "launch function within the scope to easily and safely manage asynchronous operations.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "When you use rememberCoroutineScope, it creates a CoroutineScope that is tied to the lifecycle of the Composable function. This means that any coroutines launched within this scope will be automatically cancelled when the Composable function is removed from the composition hierarchy.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "The launch function creates a new coroutine within the CoroutineScope, and automatically manages its lifecycle. If the Composable function is removed from the composition hierarchy, any coroutines launched within this scope will be automatically cancelled.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "LaunchedEffect requires a composable scope to run, it can’t be used in cases such as onClick() of a button(onClick lambda function goes out of compose scope and doesn’t have coroutine scope). That is where rememberCoroutineScope comes into play. rememberCoroutineScope creates a coroutine scope to call our actions outside of compose. Moreover, this scope will be bound to composable’s lifecycle so that when composition leaves the screen(Navigating, app termination and etc.) scope will be canceled automatically.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create a coroutine scope associated with the current composition, where we can call any suspended function inside it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutine scope to run functions outside of compose scope. LaunchEffect fire & forget works. If you are going to take manual actions such as cancel and run again, you can use rememberCoroutineScope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope is a composable function which returns a scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Basically, a CoroutineScope which remember (cache) it for the Composable, is also tied to its lifecycle and gets cancelled once the Composable leaves the composition. Useful in handling jobs in response to callbacks or events.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This coroutine scope can be used to launch new coroutines that are automatically cancelled when the composition (composable function) is no longer active.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The CoroutineScope object created by rememberCoroutineScope() is a singleton for each composition. This means that if the function is called multiple times within the same composition, it will return the same coroutine scope object.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Additionally, rememberCoroutineScope automatically cleans up the CoroutineScope object, preventing unnecessary memory usage. Therefore, you do not have to manually clean up the CoroutineScope object, and the performance of the application improves.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //LaunchedEffect, it lets you launch coroutines on demand, such as in response to button clicks or user events.
                    //You can't call suspend functions directly inside onClick. This is where rememberCoroutineScope comes in.
                    //The scope is tied to this composable’s lifecycle — if the composable leaves the screen, the scope cancels automatically. You’re safe.
                    
                    @Composable
                    fun ButtonWithCoroutine() {
                        val coroutineScope = rememberCoroutineScope() // Obtain the coroutine scope

                        Button(onClick = {
                            coroutineScope.launch {
                                // Perform a coroutine operation
                                delay(1200) // Simulate a delay
                                Log.d("Coroutine", "Task completed!")

                                //showDialog()
                                //navigateToNextScreen()
                                //isValidInput()
                                //databaseOperation()
                                //animationContentSize()

                            }
                        }) {
                            Text("Click")
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope: Provides a CoroutineScope tied to the composable's lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "coroutineScope.launch: Launches a coroutine within the provided scope when the button is clicked.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    onAppleLoginClick = {
                        if (loginUiState.isLoading) {
                            return@LoginScreen
                        }
                    
                        coroutineScope.launch {
                            val appleLoginResult = loginWithApple()
                            val appleToken = appleLoginResult.getOrNull()?.trim().orEmpty()
                            if (appleToken.isBlank()) {
                                loginViewModel.onLoginFailed()
                                return@launch
                            }
                            loginViewModel.loginWithAppleIdentityToken(identityToken = appleToken)
                        }
                    }
                    
                    sealed interface LoginSideEffect {
                        data object LoginFailed : LoginSideEffect
                    }

                    class LoginViewModel(
                        private val loginWithKakaoAccessTokenUseCase: LoginWithKakaoAccessTokenUseCase,
                        private val loginWithGoogleIdentityTokenUseCase: LoginWithGoogleIdentityTokenUseCase,
                        private val loginWithAppleIdentityTokenUseCase: LoginWithAppleIdentityTokenUseCase,
                    ) : ViewModel() {
                        private val _sideEffect = MutableSharedFlow<LoginSideEffect>(extraBufferCapacity = 1)
                        val sideEffect = _sideEffect.asSharedFlow()

                        fun onLoginFailed() {
                            _sideEffect.tryEmit(LoginSideEffect.LoginFailed)
                        }
                    }    
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun RememberCoroutineScopeScreen(
                        snackbarHostState: SnackbarHostState = remember { SnackbarHostState() },
                    ) {
                        val coroutineScope = rememberCoroutineScope()

                        Scaffold(
                            modifier = Modifier.height(200.dp), //
                            snackbarHost = { SnackbarHost(hostState = snackbarHostState) }
                        ) {
                            Column(
                                modifier = Modifier.padding(it)
                            ) {
                                Button(onClick = {
                                    coroutineScope.launch {
                                        val snackbarResult = snackbarHostState.showSnackbar(
                                            message = "Show!",
                                            actionLabel = "Action",
                                            duration = SnackbarDuration.Short
                                        )

                                        when (snackbarResult) {
                                            SnackbarResult.Dismissed -> {
                                                logger { "Dismissed" }
                                            }

                                            SnackbarResult.ActionPerformed -> {
                                                logger { "ActionPerformed" }
                                            }
                                        }
                                    }
                                }) {
                                    Text(text = "show SnackBar")
                                }
                            }
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    /*
                        In this example, the scope variable is created using the rememberCoroutineScope composable. This ensures that the coroutine scope will be cancelled when the CounterComponent() composable function is removed from the composition hierarchy. This prevents the memory leak from occurring.
                    */

                    @Composable
                    fun RememberCoroutineScopeTestScreen ( ) {
                        Column(
                            modifier = Modifier.fillMaxSize(),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            val coroutineScope = rememberCoroutineScope()
                            var counter by remember { mutableStateOf(0) }

                            Text(text = counter.toString())
                            Spacer(modifier = Modifier.height(8.dp))
                            Button(
                                onClick = {
                                    coroutineScope.launch {
                                        counter += 1
                                    }
                                }
                            ) {
                                Text(text = "Button")
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun SearchScreen() {
                      val scope = rememberCoroutineScope()
                      var currentJob by remember { mutableStateOf<Job?>(null) }
                      var items by remember { mutableStateOf<List<Item>>(emptyList()) }

                      Column {
                        Row {
                          TextInput(
                            afterTextChange = { text ->
                              currentJob?.cancel()
                              currentJob = scope.async {
                                delay(threshold)
                                items = viewModel.search(query = text)
                              }
                            }
                          )
                        }
                        Row { ItemsVerticalList(items) }
                      }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Button to Go To Bottom of the list
                    Button(onClick = {
                      coroutineScope.launch { lazyListState.animateScrollToItem(messages.size - 1) }
                    }) {
                      Text(text = "Go To Bottom")
                    }

                    // Button to Go To Top of the list
                    Button(onClick = {
                      coroutineScope.launch { lazyListState.animateScrollToItem(0) }
                    }) {
                      Text(text = "Go To Top")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    Button(
                        enabled = prevButtonVisible.value,
                        onClick = {
                            val prevPageIndex = pagerState.currentPage - 1
                            coroutineScope.launch { pagerState.animateScrollToPage(prevPageIndex) }
                        },
                    ) {
                        Text(text = "Prev")
                    }
                    
                    Button(
                        enabled = nextButtonVisible.value ,
                        onClick = {
                            val nextPageIndex = pagerState.currentPage + 1
                            coroutineScope.launch { pagerState.animateScrollToPage(nextPageIndex) }
                        },
                    ) {
                        Text(text = "Next")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun CameraCapture(onImageCaptured: (Uri) -> Unit) {
                        val coroutineScope = rememberCoroutineScope()
                        val context = LocalContext.current
                        val imageUri = remember { mutableStateOf<Uri?>(null) }

                        Button(onClick = {
                            coroutineScope.launch {
                                val uri = capturePhoto(context)
                                imageUri.value = uri
                                onImageCaptured(uri)
                            }
                        }) {
                            Text("Capture Photo")
                        }

                        imageUri.value?.let { uri ->
                            Image(
                                painter = rememberImagePainter(uri),
                                contentDescription = "Captured Image",
                                modifier = Modifier.size(200.dp)
                            )
                        }
                    }

                    suspend fun capturePhoto(context: Context): Uri {
                        // Simulate photo capture process and return a URI
                        delay(1000)
                        return Uri.parse("file://dummy/path/to/image.jpg")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //New CoroutineScope object is created with rememberCoroutineScope. Then, a process that needs to be executed in a separate thread is initiated by clicking on the Button widget. This process makes an API request with the help of RetrofitInstance and stores its result in the apiResult mutableStateOf object. As a result, with the help of Compose’s rememberCoroutineScope feature, managing long-running processes such as network operations like API calls becomes easier and provides a more efficient development experience.

                    @Composable
                    fun ApiCallScreen() {
                        val coroutineScope = rememberCoroutineScope()
                        val apiResult = remember { mutableStateOf<Result?>(null) }

                        Column(
                            modifier = Modifier.fillMaxSize(),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Button(
                                onClick = {
                                    coroutineScope.launch {
                                        try {
                                            val apiService = RetrofitInstance.apiService
                                            val response = apiService.getResult()
                                            if (response.isSuccessful) {
                                                apiResult.value = response.body()
                                            } else {
                                                apiResult.value = Result.Error("API Error")
                                            }
                                        } catch (e: Exception) {
                                            apiResult.value = Result.Error(e.message ?: "Unknown Error")
                                        }
                                    }
                                }
                            ) {
                                Text("API Call")
                            }

                            Spacer(modifier = Modifier.height(16.dp))

                            when (val result = apiResult.value) {
                                is Result.Success -> {
                                    Text(result.data)
                                }
                                is Result.Error -> {
                                    Text(result.message)
                                }
                                null -> {}
                            }
                        }
                    }

                    sealed class Result {
                        data class Success(val data: String) : Result()
                        data class Error(val message: String) : Result()
                    }
                    """.trimIndent()
            )

            WebImage(
                src = Resources.Images.SIDE_EFFECT_VS_1,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "LaunchedEffect VS rememberCoroutineScope"
            )

            SpanText(
                text = "When to use rememberCoroutineScope",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handling button clicks that need async work.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Actions: pull-to-refresh, swipe actions, Button clicks",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Responding to user gestures with background tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Managing user-triggered animations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "rememberCoroutineScope Key Features",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Prevents unnecessary restarts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ensures you always call the latest lambda.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Can be used outside composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Automatically cancels coroutines when the composable leaves the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handle user interactions asynchronously.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launch tasks that shouldn’t automatically cancel on recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Trigger delayed actions or animations on demand.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How It Works",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composition Awareness: The coroutine scope created by rememberCoroutineScope is scoped to the composition. This ensures that any coroutines launched within this scope are canceled when the composable is removed from the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Management: The remember API is used to hold and manage state values that need to survive recomposition. When paired with rememberCoroutineScope, it helps manage asynchronous tasks safely.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Avoids Memory Leaks: Unlike using GlobalScope or manually managing coroutine scopes, rememberCoroutineScope creates a new coroutine scope and ensures that resources are cleaned up properly when the composable is no longer in use.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What Not To Do",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Don’t use rememberCoroutineScope for tasks that should not be tied to the composable lifecycle. For tasks like background work that should continue independently, use other coroutine scopes (like GlobalScope or custom scopes).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Avoid launching coroutines inside @Composable functions that don’t need to be aware of the lifecycle of the composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does rememberCoroutineScope work in handling side effects?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "rememberCoroutineScope provides a composition-aware coroutine scope that allows you to launch coroutines outside of composable functions. It ensures that the coroutine is automatically cancelled when it leaves the composition, helping manage the lifecycle of coroutines effectively.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}
